import { useTranslation } from 'react-i18next';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { AdminChartPoint } from '../../../types';

interface GrowthChartProps {
  chartData: AdminChartPoint[];
  range: 'week' | 'month' | 'year';
  onRangeChange: (range: 'week' | 'month' | 'year') => void;
  loadingChart: boolean;
}

export function GrowthChart({
  chartData,
  range,
  onRangeChange,
  loadingChart,
}: GrowthChartProps) {
  const { t } = useTranslation();
  const newUsersInRange = chartData.reduce((sum, d) => sum + d.newUsers, 0);

  return (
    <div className="admin-chart-section" style={{ opacity: loadingChart ? 0.6 : 1, transition: 'opacity 0.2s' }}>
      <div className="admin-chart-header">
        <div>
          <h3 className="admin-chart-title">{t('admin.userGrowth')}</h3>
          <p className="admin-chart-subtitle">
            <span className="admin-chart-highlight">+{newUsersInRange}</span> {t('admin.newUsersInRange')}
          </p>
        </div>
        <div className="admin-chart-tabs">
          <button className={`admin-chart-tab ${range === 'week' ? 'active' : ''}`} onClick={() => onRangeChange('week')}>
            {t('admin.week')}
          </button>
          <button className={`admin-chart-tab ${range === 'month' ? 'active' : ''}`} onClick={() => onRangeChange('month')}>
            {t('admin.month')}
          </button>
          <button className={`admin-chart-tab ${range === 'year' ? 'active' : ''}`} onClick={() => onRangeChange('year')}>
            {t('admin.year')}
          </button>
        </div>
      </div>

      <div className="admin-chart-container">
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#9c2426" stopOpacity={0.15}/>
                <stop offset="95%" stopColor="#9c2426" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorNew" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.15}/>
                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#8c8c9a' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: '#8c8c9a' }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
              labelStyle={{ fontWeight: 600, marginBottom: 4 }}
            />
            <Area type="monotone" dataKey="totalUsers" name={t('admin.totalUsers')} stroke="#9c2426" strokeWidth={2.5} fill="url(#colorTotal)" />
            <Area type="monotone" dataKey="newUsers" name={t('admin.newUsersLabel')} stroke="#f59e0b" strokeWidth={2.5} fill="url(#colorNew)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
